import { t } from "i18next";
import { useState, useRef, MutableRefObject, useEffect, useCallback, ReactNode } from "react";
import styles from "./CrmGridProcessSelectedRowsButton.module.scss";
import { ReactComponent as ProcessIcon } from '@assets/Icons/table-icon.svg';
import { ReactComponent as CloseBtnSvg } from '@assets/Icons/close-button-icon.svg';
import { Overlay } from "react-overlays";
import { PrimaryButton } from "@core/VisualComponents/Buttons/PrimaryButton";
import { useAppSelector } from "@core/Redux/hooks";
import { selectPluginsState } from "@core/Redux/store";
import { IMultiEditor } from "@pluginShared/i-plugin-contract";
import { loadedMultiEditors } from "@core/Plugins/pluginManager";
import { BulkUpdateApi, BulkUpdateStatus, IBulkUpdateResult } from "@core/EventSourcing/BulkUpdateApi";
import { Result, Spin } from "antd";
import React from "react";
import Logger from "js-logger";

export interface ICrmGridProcessSelectedRowsButtonProps {
    bulkUpdateApi: BulkUpdateApi;
    tableId: string;
}

export function CrmGridProcessSelectedRowsButton(props: ICrmGridProcessSelectedRowsButtonProps) {
    const [showProcessPluginsList, setShowProcessPluginsList] = useState<boolean>(false);
    const processPluginsListButtonRef = useRef<any>();

    const HandleShowProcessPluginsList = () => {
        setShowProcessPluginsList(prev => !prev);
    };

    return <>
        {props.bulkUpdateApi.length > 0 && <>
            <div ref={processPluginsListButtonRef} onClick={HandleShowProcessPluginsList} className={styles.button}>
                <ProcessIcon/>
                <span>{t("process-selected")}</span>
            </div>
            {showProcessPluginsList &&
                <CrmGridProcessSelectedRowsPanel
                    onHide={() => setShowProcessPluginsList(false)}
                    targetRef={processPluginsListButtonRef}
                    bulkUpdateApi={props.bulkUpdateApi}
                    tableId={props.tableId}
                />
            }
        </>}
    </>;
}

interface ICrmGridProcessSelectedRowsPanelProps {
    onHide: () => void;
    targetRef: MutableRefObject<any>;
    bulkUpdateApi: BulkUpdateApi;
    tableId: string;
}

function CrmGridProcessSelectedRowsPanel(props: ICrmGridProcessSelectedRowsPanelProps) {
    const [selectedEditor, setSelectedEditor] = useState<IMultiEditor | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [result, setResult] = useState<IBulkUpdateResult | null>(null);

    const pluginsState = useAppSelector(selectPluginsState);

    const multiEditors: IMultiEditor[] = pluginsState.modules
        .flatMap(module => 
            module.installation.multiEditors.map(x => loadedMultiEditors[x.id ?? ""])
        )
        .filter(editor => editor.tableId == props.tableId);

    const tooManyRows = props.bulkUpdateApi.length > BulkUpdateApi.rowLimit;

    const onStartUpdate = useCallback(() => {
        setIsLoading(true);
    }, [setIsLoading]);

    const onAfterUpdate = useCallback((result: IBulkUpdateResult) => {
        setIsLoading(false);
        setResult(result);
    }, [setIsLoading, setResult]);

    useEffect(() => {
        props.bulkUpdateApi.handleOnStartUpdate(onStartUpdate);
        props.bulkUpdateApi.handleOnAfterUpdate(onAfterUpdate);

        return () => {
            props.bulkUpdateApi.unhandleOnStartUpdate(onStartUpdate);
            props.bulkUpdateApi.unhandleOnAfterUpdate(onAfterUpdate);
        }
    }, [onStartUpdate, onAfterUpdate]);

    useEffect(() => {
        if (selectedEditor == null) {
            props.bulkUpdateApi.setCreationContext(null);
        }
        props.bulkUpdateApi.setCreationContext({ pluginId: selectedEditor?.pluginId ?? null, editorCaption: selectedEditor?.caption });

        return () => {
            props.bulkUpdateApi.setCreationContext(null);
        }
    }, [selectedEditor]);

    const keydownHandler = (event: KeyboardEvent) => {
        switch (event.key) {
            case "Escape":
                onHide();
                break;
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", keydownHandler, false);
        return () => {
            document.removeEventListener("keydown", keydownHandler, false);
        };
    }, [keydownHandler]);

    const onHide = () => {
        if (!isLoading) {
            props.onHide();
        }
    }

    return (<Overlay
        show={true}
        flip={true}
        placement={'bottom-start'}
        rootClose={true}
        onHide={onHide}
        target={props.targetRef}
        popperConfig={popperConfig}
    >
        {({ show, props: overlayProps, arrowProps, placement }) => 
            <div {...overlayProps} className={styles.host}>
                <div className={styles.component}>
                    {result == null
                        ? <Spin spinning={isLoading}>
                            <span>
                                {t("selected-count")}:&nbsp;
                                <span style={{
                                    color: tooManyRows ? "red" : undefined,
                                    fontWeight: "bold",
                                }}>{props.bulkUpdateApi.length}</span>
                            </span>
                            <br/><br/>
                            {tooManyRows
                                ? <span>{t("too-many-rows-part1")} {BulkUpdateApi.rowLimit} {t("too-many-rows-part2")}</span>
                                : <>{selectedEditor == null
                                    ? <div>
                                        <h1>{t("select-plugin")}:</h1>
                                        <ul>{multiEditors.map(editor => <li key={editor.id}>
                                            <br/>
                                            <a onClick={() => setSelectedEditor(editor)}>{editor.caption}</a>
                                        </li>)}</ul>
                                    </div>
                                    : <div>
                                        <a onClick={() => setSelectedEditor(null)}>{t("back")}</a>
                                        <h1>{selectedEditor.caption}</h1>
                                        <ErrorBoundary>
                                            <selectedEditor.func selectedEntities={props.bulkUpdateApi} tableId={props.tableId}/>
                                        </ErrorBoundary>
                                    </div>
                                }</>
                            }
                        </Spin>
                        : <Result
                            status={result.status == BulkUpdateStatus.Done ? "success" : "error"}
                            title={result.status == BulkUpdateStatus.Done ? t("success") : t("error")}
                            subTitle={`${t("completed-rows")}: ${result.numberOfCompleted}`}
                            extra={[
                                <PrimaryButton key="0" onClick={onHide}>{t("close")}</PrimaryButton>
                            ]}
                        />
                    }
                    <button type="button"
                            className={styles.closeButton}
                            onClick={onHide}>
                        <CloseBtnSvg />
                    </button>
                </div>
            </div>
        }
    </Overlay>)
}

const popperConfig = {
    modifiers: [
        {
            name: 'preventOverflow',
            options: {
                boundary: 'viewport',
            },
        },
        {
            name: 'flip',
            options: {
                fallbackPlacements: ['left', 'right', 'bottom'],
            },
        },
    ]
};

interface ErrorBoundaryProps {
    children: React.ReactNode;
}
  
interface ErrorBoundaryState {
    hasError: boolean;
    errorMessage: string;
}
  
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }
  
    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Обновляем состояние для отображения пользовательского интерфейса ошибки
        return { hasError: true, errorMessage: error.message };
    }
  
    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Логирование ошибки или других побочных эффектов
        console.error('PluginError:', error, errorInfo);
    }
  
    render() {
        if (this.state.hasError) {
            // Если ошибка произошла, отобразим сообщение красным текстом
            return <h1 style={{ color: 'red' }}>Error: {this.state.errorMessage}</h1>;
        }
    
        return this.props.children;
    }
}
